import { useEffect, useRef } from 'react';
import { sayAloud } from '../lib/speech.js';
import { Navigate, useNavigate } from 'react-router-dom';
import { reloadIfUpdateWaiting } from '../lib/update.js';
import { findCompanion } from '@qq/shared';
import { childPhotoUrl, useLogout, useSession } from '../lib/session.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Avatar } from '../art/Avatar.js';
import { Companion } from '../art/Companion.js';
import { Collectible } from '../art/Collectible.js';
import { MoodFace } from '../art/MoodFace.js';

/**
 * The signed-in child's home. Phase 2 shows their character and their companion
 * and tells the truth about where the adventure is up to; the mission map
 * replaces the middle of this screen in Phase 4.
 */
interface SiblingInfo {
  siblings: { nickname: string; isExploringNow: boolean; collectiblesFound: number }[];
  participation: { summary: string } | null;
}

/**
 * What one child sees about the other: whether they are playing, how many
 * adventures each has done against their OWN target, and how big their
 * collection is. Nothing academic, ever.
 */
function SiblingStrip() {
  const siblings = useQuery({
    queryKey: ['siblings'],
    queryFn: () => api<SiblingInfo>('/child/siblings'),
  });
  const nudge = useQuery({
    queryKey: ['nudge'],
    queryFn: () => api<{ nudge: string | null }>('/child/nudge'),
  });

  const other = siblings.data?.siblings[0];

  return (
    <>
      {nudge.data?.nudge && <p className="gentle-note">{nudge.data.nudge}</p>}
      {other?.isExploringNow && <p className="pill">{other.nickname} is exploring now</p>}
      {siblings.data?.participation && (
        <p className="pill">{siblings.data.participation.summary}</p>
      )}
    </>
  );
}

/**
 * Whether today's check-in still needs doing.
 *
 * `undefined` while it is unknown - which matters, because "not answered yet"
 * and "we could not ask the server" must not be treated the same. If the check
 * itself fails, the child goes straight to their adventure rather than being
 * held at a gate nobody can open.
 */
function useCheckInDue(): boolean | undefined {
  const today = useQuery({
    queryKey: ['checkin', 'today'],
    queryFn: () => api<{ done: boolean }>('/child/checkin/today'),
  });
  if (today.isPending || today.isError) return undefined;
  return today.data?.done === false;
}

/**
 * Today's real-world quest, if it is still outstanding.
 *
 * An invitation, never a gate. The quest happens in a kitchen or on a
 * footpath; holding a child at a screen until they tick it would be exactly
 * backwards.
 */
function QuestInvite() {
  const navigate = useNavigate();
  const quest = useQuery({
    queryKey: ['quest', 'today'],
    queryFn: () => api<{ quest: { title: string; status: string } | null }>('/child/quest'),
  });

  const today = quest.data?.quest;
  if (!today || today.status === 'done') return null;

  return (
    <button type="button" className="quest-invite" onClick={() => navigate('/quest')}>
      <span className="quest-invite__flag">Out in the real world</span>
      <span>{today.title}</span>
    </button>
  );
}

function CheckInInvite({ due }: { due: boolean | undefined }) {
  const navigate = useNavigate();
  if (!due) return null;

  return (
    <button type="button" className="checkin-invite" onClick={() => navigate('/checkin')}>
      <MoodFace mood="ok" size={56} title="" />
      <span>How are you feeling today?</span>
    </button>
  );
}

export function ChildHome() {
  const navigate = useNavigate();
  const { data, isPending } = useSession();
  const logout = useLogout('child');
  const checkInDue = useCheckInDue();

  // Home is between missions: the safest place to pick up a new version.
  useEffect(() => {
    void reloadIfUpdateWaiting();
  }, []);

  // A child with cards and no buddy is invited to choose one - out loud for a
  // child who is read to, once per visit, because the button's words are not
  // something a pre-reader can use.
  const inviteBuddy = Boolean(data?.child && !data.child.buddy && data.child.cardsOwned > 0);
  const invited = useRef(false);
  useEffect(() => {
    if (!inviteBuddy || invited.current || data?.child?.readAloud !== 'auto') return;
    invited.current = true;
    sayAloud({ text: 'Choose a buddy to come along!' });
  }, [inviteBuddy, data?.child?.readAloud]);

  if (isPending) return <main className="screen">Just a moment&hellip;</main>;
  const child = data?.child;
  if (!child) return <Navigate to="/" replace />;

  const companion = findCompanion(child.companionId);
  const showPhoto = child.avatarKind === 'photo' && child.hasPhoto;

  return (
    <main className="screen">
      <div className="builder-preview">
        <Avatar
          config={child.avatarConfig}
          size={168}
          photoUrl={showPhoto ? childPhotoUrl(child.id, child.photoUpdatedAt) : null}
          title={`${child.nickname}'s character`}
        />
        <Companion companionKey={child.companionId} size={104} title={companion?.name} />
        {child.buddy && (
          <Collectible collectibleKey={child.buddy.key} size={96} title={child.buddy.name} />
        )}
      </div>

      <h1>You&rsquo;re in, {child.nickname}!</h1>
      {companion && (
        <p>
          {companion.name} is coming with you. {companion.blurb}
        </p>
      )}
      {child.buddy && <p className="buddy-line">🤝 {child.buddy.name} is your buddy today!</p>}
      {inviteBuddy && (
        <button type="button" className="btn btn--big" onClick={() => navigate('/shelf')}>
          🤝 Choose a buddy!
        </button>
      )}
      <CheckInInvite due={checkInDue} />

      {/* The adventure goes through the check-in first when today's is still
          outstanding. Asked once a day, not once a session: answering in the
          morning does not mean being asked again after lunch. */}
      <button
        type="button"
        className="btn btn--big"
        onClick={() => navigate(checkInDue ? '/checkin' : '/play')}
      >
        Start an adventure
      </button>

      <QuestInvite />

      <div className="selfie-actions">
        <button type="button" className="btn btn--outline" onClick={() => navigate('/shelf')}>
          My shelf
        </button>
        <button type="button" className="btn btn--outline" onClick={() => navigate('/character')}>
          Change my character
        </button>
      </div>

      <button
        type="button"
        className="btn btn--quiet"
        onClick={() =>
          logout.mutate(undefined, { onSuccess: () => navigate('/', { replace: true }) })
        }
      >
        Finish for now
      </button>
    </main>
  );
}
